import type {
  ApiListResponse,
  ApiResponse,
  ListMeta,
  ProblemDetails,
} from '@rodzinne-archiwum/shared';

import { humanizeServerMessage } from './humanize-server-message';

/**
 * Wspólna warstwa wywołań `/api/v1` dla zapisów z etapu 6 (§12.1, §17.4).
 *
 * Dotąd każda funkcja wołała `fetch` sama (`features/auth/auth-api.ts`,
 * `features/tree/tree-api.ts`) i wystarczało to, bo etapy 1–5 tylko CZYTAŁY. Zapis wprowadza
 * trzy rzeczy, których nie wolno powtarzać w każdym formularzu osobno, bo pomyłka w
 * którejkolwiek z nich kończy się CICHĄ utratą cudzej edycji:
 *
 * 1. `If-Match` przy każdej mutacji — §12.1 i §17.4: „Optimistic concurrency z §12 (`If-Match`)
 *    zapobiega cichemu nadpisaniu równoczesnej edycji”. Serwer odpowiada 428, gdy nagłówka
 *    brakuje, i 412, gdy wersja jest nieaktualna.
 * 2. Rozpoznanie 412 jako ODRĘBNEJ sytuacji, nie zwykłego błędu: odpowiedź niesie
 *    `currentVersion`, dzięki czemu interfejs może pokazać konflikt równoczesnej edycji zamiast
 *    komunikatu „coś poszło nie tak”.
 * 3. Odczyt `ProblemDetails` — backend zwraca błędy w jednym formacie (`code`, `title`,
 *    `detail`), więc komunikat dla użytkownika ma pochodzić z odpowiedzi, a nie być zgadywany
 *    po samym kodzie HTTP.
 *
 * `credentials: 'same-origin'` jest tu WYMAGANE, nie ozdobne: sesja jest w ciasteczku HttpOnly
 * (§17.2), a aplikacja i API dzielą jeden origin (§17.3, w developmencie przez proxy Vite).
 */

/** Błąd wywołania API niosący treść `ProblemDetails` z serwera, gdy serwer ją przysłał. */
export class ApiError extends Error {
  readonly status: number;
  readonly problem: ProblemDetails | null;

  constructor(status: number, problem: ProblemDetails | null, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.problem = problem;
  }

  /** Kod domenowy z `ProblemDetails` (np. `person_has_dependencies`), gdy serwer go podał. */
  get code(): string | null {
    return this.problem?.code ?? null;
  }
}

/**
 * Konflikt równoczesnej edycji (HTTP 412) — wydzielony z {@link ApiError}, żeby interfejs mógł
 * go obsłużyć INACZEJ niż pozostałe błędy: §17.4 wymaga pokazania, że ktoś zmienił ten rekord w
 * międzyczasie, a nie potraktowania tego jak awarii. `currentVersion` to wersja, którą serwer ma
 * teraz — pozwala odświeżyć widok i ponowić zapis świadomie.
 */
export class ConcurrentEditError extends ApiError {
  readonly currentVersion: number | null;

  constructor(problem: ProblemDetails | null, message: string) {
    super(412, problem, message);
    this.name = 'ConcurrentEditError';
    this.currentVersion = problem?.currentVersion ?? null;
  }
}

/** Brak nagłówka `If-Match` przy mutacji (HTTP 428) — zawsze błąd programistyczny po naszej stronie. */
export class MissingVersionError extends ApiError {
  constructor(problem: ProblemDetails | null) {
    super(
      428,
      problem,
      'Zapis bez nagłówka If-Match: mutacja musi podać wersję rekordu. ' +
        'To błąd w kodzie klienta, nie sytuacja do pokazania użytkownikowi.',
    );
    this.name = 'MissingVersionError';
  }
}

export interface RequestOptions {
  /** Wersja rekordu do `If-Match`; wymagana przy PATCH/DELETE/POST zmieniającym istniejący rekord. */
  readonly version?: number;
  /** Treść żądania; pominięta dla żądań bez ciała (np. `POST .../restore`). */
  readonly body?: unknown;
  readonly signal?: AbortSignal;
}

async function readProblem(response: Response): Promise<ProblemDetails | null> {
  try {
    return (await response.json()) as ProblemDetails;
  } catch {
    // Odpowiedź bez poprawnego JSON-a (np. błąd serwera proxy) — brak treści problemu nie może
    // przesłonić samego faktu niepowodzenia, więc zwracamy `null` i zostaje status HTTP.
    return null;
  }
}

function describe(status: number, problem: ProblemDetails | null): string {
  const detail = problem?.detail ?? problem?.title ?? null;
  return detail === null
    ? `Żądanie nie powiodło się (HTTP ${String(status)}).`
    : humanizeServerMessage(detail);
}

async function request(method: string, path: string, options: RequestOptions): Promise<unknown> {
  const headers: Record<string, string> = {};
  if (options.body !== undefined) {
    headers['content-type'] = 'application/json';
  }
  if (options.version !== undefined) {
    // Cudzysłowy są częścią składni ETag-a, której oczekuje `api/if-match.ts` po stronie serwera.
    headers['if-match'] = `"${String(options.version)}"`;
  }

  const response = await fetch(path, {
    method,
    credentials: 'same-origin',
    headers,
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
    ...(options.signal === undefined ? {} : { signal: options.signal }),
  });

  if (response.ok) {
    return response.status === 204 ? null : await response.json();
  }

  const problem = await readProblem(response);
  if (response.status === 412) {
    throw new ConcurrentEditError(problem, describe(412, problem));
  }
  if (response.status === 428) {
    throw new MissingVersionError(problem);
  }
  throw new ApiError(response.status, problem, describe(response.status, problem));
}

export async function apiGet<T>(path: string, signal?: AbortSignal): Promise<ApiResponse<T>> {
  return (await request('GET', path, signal === undefined ? {} : { signal })) as ApiResponse<T>;
}

export async function apiGetList<T>(
  path: string,
  signal?: AbortSignal,
): Promise<ApiListResponse<T>> {
  return (await request('GET', path, signal === undefined ? {} : { signal })) as ApiListResponse<T>;
}

/**
 * Odpowiedź `data` niebędąca tablicą, ale wciąż niosąca `nextCursor` w `meta` — kształt
 * `GET /api/v1/search` (§12.2: „data: {family, research}”, `meta.nextCursor`), różny zarówno od
 * {@link ApiResponse} (bez `nextCursor`), jak i {@link ApiListResponse} (którego `data` MUSI być
 * tablicą — `T[]`, patrz `packages/shared/src/api.ts`). Zamiast obchodzić `apiGet`/`apiGetList`
 * rzutowaniem w miejscu wywołania (czego zakaz zadania wprost wymaga — „jeżeli czegoś w niej
 * brakuje, dopisz do niej”), ta funkcja jest trzecim, jawnym kształtem odpowiedzi listowej.
 */
export interface ApiSearchResponse<T> {
  data: T;
  meta: ListMeta;
}

export async function apiGetSearch<T>(
  path: string,
  signal?: AbortSignal,
): Promise<ApiSearchResponse<T>> {
  return (await request(
    'GET',
    path,
    signal === undefined ? {} : { signal },
  )) as ApiSearchResponse<T>;
}

export async function apiPost<T>(
  path: string,
  options: RequestOptions = {},
): Promise<ApiResponse<T>> {
  return (await request('POST', path, options)) as ApiResponse<T>;
}

export interface MultipartRequestOptions {
  /** Ciało `multipart/form-data` — NIGDY `JSON.stringify`-owane (w odróżnieniu od
   * {@link RequestOptions.body}), bo `fetch` sam ustawia `Content-Type` z poprawną granicą
   * WYŁĄCZNIE gdy ciało jest `FormData` i nagłówek nie jest ustawiony ręcznie. */
  readonly body: FormData;
  readonly signal?: AbortSignal;
}

/**
 * Jedyna droga wysłania `multipart/form-data` (dziś: upload załącznika, `POST /attachments`,
 * §12.2) — {@link request} wyżej zakłada ciało JSON (`JSON.stringify`, nagłówek `content-type:
 * application/json`), co rozbiłoby granicę multipart. Ta funkcja świadomie NIE ustawia
 * `content-type` — przeglądarka dopisuje go sama, z poprawną granicą (`boundary=...`), TYLKO gdy
 * nagłówek jest nieustawiony i ciało jest `FormData`.
 */
export async function apiPostMultipart<T>(
  path: string,
  options: MultipartRequestOptions,
): Promise<ApiResponse<T>> {
  const response = await fetch(path, {
    method: 'POST',
    credentials: 'same-origin',
    body: options.body,
    ...(options.signal === undefined ? {} : { signal: options.signal }),
  });

  if (response.ok) {
    return (await response.json()) as ApiResponse<T>;
  }

  const problem = await readProblem(response);
  if (response.status === 412) {
    throw new ConcurrentEditError(problem, describe(412, problem));
  }
  if (response.status === 428) {
    throw new MissingVersionError(problem);
  }
  throw new ApiError(response.status, problem, describe(response.status, problem));
}

export async function apiPatch<T>(path: string, options: RequestOptions): Promise<ApiResponse<T>> {
  return (await request('PATCH', path, options)) as ApiResponse<T>;
}

export async function apiDelete<T>(path: string, options: RequestOptions): Promise<ApiResponse<T>> {
  return (await request('DELETE', path, options)) as ApiResponse<T>;
}
